import { Button } from "primereact/button";
import { confirmPopup } from "primereact/confirmpopup";
import { DataView } from "primereact/dataview";
import { Panel } from "primereact/panel";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ToastContext } from "src/core/context/ToastContext";
import { useErrorHandler } from "src/core/error/useErrorHandler";
import { useDate } from "src/core/hooks/useDate";
import { useError } from "src/core/hooks/useError";
import { logService } from "src/core/services/logService";
import { textService } from "src/core/services/textService";
import { useAppointmentStore } from "src/features/appointment/services/useAppointmentStore";
import { ParticipantEdit } from "src/features/participant/components/ParticipantEdit/ParticipantEdit";
import { ParticipantModel } from "src/features/participant/models/participantModel";
import { useParticipantStore } from "../../../../../participant/services/useParticipantStore";
import "./HomeParticipants.css";

export function HomeParticipants() {
  const toastRef = useContext(ToastContext);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { dateFormat } = useDate();
  const { getErrorMessage } = useError();

  const participantStore = useParticipantStore();
  const appointmentStore = useAppointmentStore();
  const errorHandler = useErrorHandler();

  const [participants, setParticipants] = useState<ParticipantModel[]>();
  const [selectedParticipant, setSelectedParticipant] =
    useState<ParticipantModel>();
  const [participantEditVisible, setParticipantEditVisible] = useState(false);

  useEffect(() => {
    participantStore.participantList();
  }, []);

  useEffect(() => {
    const participants = participantStore.participants;

    if (logService.globalLog) logService.log("participants", participants);

    setParticipants(participants);
  }, [participantStore.participants]);

  const onParticipantAdd = () => {
    setSelectedParticipant(new ParticipantModel());
    setParticipantEditVisible(true);
  };

  const onParticipantEdit = (participant: ParticipantModel) => {
    if (!participant.id || participant.id === 0) return;

    setSelectedParticipant(participant);
    setParticipantEditVisible(true);
  };

  const onParticipantDelete = (
    event: React.MouseEvent<HTMLButtonElement>,
    participant: ParticipantModel
  ) => {
    logService.log("onParticipantDelete", participant);

    if (!participant.id || participant.id === 0) return;

    if (
      appointmentStore.appointments.some(
        (appointment) => appointment.participantId === participant.id
      )
    ) {
      errorHandler.setError({
        title: t("Home.Participants.Title"),
        message: t("Home.Participants.DeleteFailedExistingAppointments"),
      });
      return;
    }

    confirmPopup({
      target: event.target as HTMLElement,
      message: t("Home.Participants.DeleteSure"),
      icon: "pi pi-question-circle",
      defaultFocus: "accept",
      accept: async () => {
        participant.isLoading = true;

        await participantStore
          .participantDelete(participant.id)
          .then(() => {
            toastRef?.current?.show({
              severity: "success",
              summary: t("Home.Participants.Title"),
              detail: t("Home.Participants.DeleteSuccess"),
              life: 5000,
            });

            participantStore.participantList();
          })
          .catch((error) => {
            errorHandler.setError({
              title: t("Home.Participants.Title"),
              message:
                t("Home.Participants.DeleteFailed") +
                " " +
                getErrorMessage(error),
            });
          });

        participant.isLoading = false;
      },
    });
  };

  const onMakeAnAppointment = (participant: ParticipantModel) => {
    if (logService.globalLog)
      logService.log("onMakeAnAppointment", participant);

    appointmentStore.setSelectedPerson(participant);

    appointmentStore.setQuickAppointment(true);

    navigate("/appointment");
  };

  return (
    <div className="homeParticipants">
      <Panel
        header={
          <div className="flex align-items-center gap-2">
            <span className="pi pi-users"></span>
            <span className="font-bold">{t("Home.Participants.Title")}</span>
          </div>
        }
        icons={
          <Button
            icon="pi pi-user-plus"
            label={t("Home.Participants.New")}
            text
            onClick={() => {
              onParticipantAdd();
            }}
          ></Button>
        }
      >
        <DataView
          value={participants}
          listTemplate={(items?: ParticipantModel[]) => {
            if (!items || items.length === 0) return null;

            let list = items.map((item, index) => {
              return (
                <div
                  key={item.id}
                  className={
                    "col-12 " + (index > 0 ? "border-top-1 surface-border" : "")
                  }
                >
                  <div className="flex flex-row align-items-center gap-3">
                    <div className="flex flex-column flex-grow-1">
                      <div className="font-bold">{item.name}</div>
                      <div className="font-medium">
                        {textService.formatDate(item.birthDate, dateFormat)}
                      </div>
                      <div>{item.ssn}</div>
                      <div className="font-italic">
                        {textService.formatAddress(item.address)}
                      </div>
                      <div>
                        <Button
                          icon="pi pi-calendar-plus"
                          label={t("Home.Participants.MakeAnAppointment")}
                          severity="contrast"
                          onClick={() => onMakeAnAppointment(item)}
                        ></Button>
                      </div>
                    </div>

                    <Button
                      icon="pi pi-user-edit"
                      severity="warning"
                      onClick={() => onParticipantEdit(item)}
                    ></Button>

                    <Button
                      icon="pi pi-trash"
                      severity="danger"
                      onClick={(event) => onParticipantDelete(event, item)}
                    ></Button>
                  </div>
                </div>
              );
            });

            return <>{list}</>;
          }}
        />
      </Panel>

      <ParticipantEdit
        visible={participantEditVisible}
        onHide={() => setParticipantEditVisible(false)}
        participant={selectedParticipant}
      />
    </div>
  );
}
